import type { SupabaseClient } from '@supabase/supabase-js';
import type { Product } from '../types';

/** Kolumner för produktlistan (kort + länk), undviker stora JSON-fält. */
export const PRODUCT_LIST_COLUMNS =
  'id,name,slug,category,price_per_day,price_on_request,quote_form_message,short_description,image,popular,featured,display_order,new_until,persons_capacity,hidden_from_products';

export const PRODUCTS_PAGE_SIZE = 24;

export type ProductsListSortKey = 'name' | 'price_low' | 'price_high' | 'category' | 'popular';

export function escapeIlikePattern(raw: string): string {
  return raw.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_');
}

type CategoryFilterableQuery = {
  eq(column: string, value: string): CategoryFilterableQuery;
};

export function applyProductsCategoryFilter<T extends CategoryFilterableQuery>(
  query: T,
  category: string
): T {
  const trimmed = category.trim();
  if (!trimmed) return query;
  return query.eq('category', trimmed) as T;
}

export function mapListRowToProduct(row: Record<string, unknown>): Product {
  const short = (row.short_description as string) || '';
  return {
    id: row.id as string,
    name: row.name as string,
    category: (row.category as string) || '',
    pricePerDay: Number(row.price_per_day) || 0,
    priceOnRequest: Boolean(row.price_on_request),
    quoteFormMessage: (row.quote_form_message as string) || undefined,
    description: short,
    shortDescription: short,
    image: (row.image as string) || '',
    gallery: [],
    popular: Boolean(row.popular),
    featured: Boolean(row.featured),
    includes: [],
    upgrades: [],
    setupTime: undefined,
    size: undefined,
    slug: (row.slug as string) || undefined,
    videos: [],
    hiddenFromProducts: Boolean(row.hidden_from_products),
    persons_capacity: row.persons_capacity != null ? Number(row.persons_capacity) : undefined,
    newUntil: (row.new_until as string) ?? null,
  };
}

function applySort(q: any, sortBy: ProductsListSortKey): any {
  switch (sortBy) {
    case 'popular':
      return q
        .order('popular', { ascending: false, nullsFirst: false })
        .order('name', { ascending: true });
    case 'name':
      return q.order('name', { ascending: true });
    case 'price_low':
      return q.order('price_per_day', { ascending: true, nullsFirst: false });
    case 'price_high':
      return q.order('price_per_day', { ascending: false, nullsFirst: false });
    case 'category':
      return q.order('category', { ascending: true }).order('name', { ascending: true });
    default:
      return q;
  }
}

/** True om söksträngen har minst en token (>= 2 alfanumeriska tecken). */
export function searchQueryHasTokens(search: string): boolean {
  const normalized = search.trim().replace(/,/g, ' ');
  if (!normalized) return false;
  const tokens = normalized.match(/[0-9A-Za-zÀ-ÖØ-öø-ÿ]{2,}/g);
  return Boolean(tokens && tokens.length > 0);
}

export function buildProductsListQuery(
  supabase: SupabaseClient,
  params: {
    category: string;
    search: string;
    sortBy: ProductsListSortKey;
    offset: number;
    pageSize: number;
  }
) {
  let q = supabase
    .from('products')
    .select(PRODUCT_LIST_COLUMNS, { count: 'exact' })
    .eq('hidden_from_products', false);

  const cat = params.category.trim();
  if (cat) {
    q = applyProductsCategoryFilter(q, cat);
  }

  // Tom sök: vanlig listquery. Icke-tom sök ska gå via fetchProductsListPage → RPC.
  const term = params.search.trim();
  if (term.length > 0) {
    const esc = escapeIlikePattern(term.replace(/,/g, ' '));
    const pattern = `%${esc}%`;
    q = q.or(`name.ilike.${pattern},short_description.ilike.${pattern}`);
  }

  q = applySort(q, params.sortBy);

  const from = params.offset;
  const to = params.offset + params.pageSize;
  return q.range(from, to);
}

export type ProductsListPageResult = {
  data: Record<string, unknown>[];
  count: number | null;
  error: { message: string } | null;
};

/**
 * Hämtar en sida produkter. Med sök används RPC search_products (token-AND + trigram).
 * Utan sök används vanlig listquery. Begär pageSize+1 rader för hasMore-detektion.
 */
export async function fetchProductsListPage(
  supabase: SupabaseClient,
  params: {
    category: string;
    search: string;
    sortBy: ProductsListSortKey;
    offset: number;
    pageSize: number;
  },
  options?: { signal?: AbortSignal }
): Promise<ProductsListPageResult> {
  const fetchLimit = params.pageSize + 1;
  const search = params.search.trim();

  if (searchQueryHasTokens(search)) {
    let rpc = supabase.rpc('search_products', {
      p_query: search,
      p_category: params.category.trim() || null,
      p_sort: params.sortBy,
      p_limit: fetchLimit,
      p_offset: params.offset,
    });

    if (options?.signal) {
      rpc = rpc.abortSignal(options.signal);
    }

    const { data, error } = await rpc;
    if (error) {
      return { data: [], count: null, error };
    }

    const rows = (data ?? []) as Record<string, unknown>[];
    const totalFromRow =
      rows.length > 0 && rows[0].total_count != null
        ? Number(rows[0].total_count)
        : 0;

    return {
      data: rows,
      count: Number.isFinite(totalFromRow) ? totalFromRow : rows.length,
      error: null,
    };
  }

  let q = buildProductsListQuery(supabase, {
    ...params,
    search: '',
    pageSize: params.pageSize,
  });

  if (options?.signal) {
    q = q.abortSignal(options.signal);
  }

  const { data, error, count } = await q;
  if (error) {
    return { data: [], count: null, error };
  }

  return {
    data: (data ?? []) as Record<string, unknown>[],
    count: typeof count === 'number' ? count : null,
    error: null,
  };
}
